import type * as api from '@owl-nest/api-client/next'
import { get } from '../utils.ts'
import { lens } from '../lens.ts'
import { type Country } from './countries.ts'

export const LANGS = ['ca', 'de', 'en', 'es', 'fr', 'it', 'nl', 'pt']

export const DEFAULT_LOCALE = get({
  key: 'DEFAULT_LOCALE',
  parse: (value: string) => {
    if (!LANGS.includes(value as any)) {
      throw Error(`value "${value}" is not a lang`)
    }
    return value as api.Lang
  },
  value: process.env.DEFAULT_LOCALE,
})

const ENVIRONMENTS = {
  DEVELOPMENT: 'development',
  PRODUCTION: 'production',
  SANDBOX: 'sandbox',
} as const

export type Environment = (typeof ENVIRONMENTS)[keyof typeof ENVIRONMENTS]

export const ENVIRONMENT =
  get({
    key: 'ENVIRONMENT',
    value: process.env.ENVIRONMENT,
    optional: true,
    parse: (env: string) => {
      if (!Object.values(ENVIRONMENTS).includes(env as Environment)) {
        return ENVIRONMENTS.PRODUCTION
      }

      return env as Environment
    },
  }) ?? ENVIRONMENTS.PRODUCTION

export const ULULE_SECURE_HTTP =
  get({
    key: 'ULULE_SECURE_HTTP',
    value: process.env.ULULE_SECURE_HTTP,
    optional: true,
  }) ?? 'false'

export const I18N_SUBDOMAINS = {
  ca: 'ca-es',
  de: 'de',
  en: 'www',
  es: 'es',
  fr: 'fr',
  it: 'it',
  nl: 'nl',
  pt: 'br',
}

export const ULULE_BASE_ORIGIN =
  get({
    key: 'ULULE_BASE_ORIGIN',
    value: process.env.ULULE_BASE_ORIGIN,
  }) ??
  window.location.hostname
    .split('.')
    .filter((domain) => !Object.values(I18N_SUBDOMAINS).includes(domain))
    .join('.')

export const ULULE_ORIGINS = (() => {
  return Object.entries(I18N_SUBDOMAINS).reduce((origins, [locale, subdomain]) => {
    const protocol = ULULE_SECURE_HTTP === 'true' ? 'https://' : 'http://'
    origins[locale as api.Lang] = `${protocol}${subdomain}.${ULULE_BASE_ORIGIN}`
    return origins
  }, {} as api.I18n)
})()

export const DEFAULT_COUNTRY = get({
  key: 'DEFAULT_COUNTRY',
  value: process.env.DEFAULT_COUNTRY,
})

export const DEFAULT_CURRENCY = get({
  key: 'DEFAULT_CURRENCY',
  value: process.env.DEFAULT_CURRENCY,
})

export const USER_LANG_COOKIE_NAME = get({
  key: 'USER_LANG_COOKIE_NAME',
  value: process.env.USER_LANG_COOKIE_NAME,
})

export const USER_CURRENCY_COOKIE_NAME = get({
  key: 'USER_CURRENCY_COOKIE_NAME',
  value: process.env.USER_CURRENCY_COOKIE_NAME,
})

export const USER_COUNTRY =
  lens<UfeConfig>().get('UFE').get('country').value(globalThis, { silent: true, forgive: true, trust: true }) ??
  DEFAULT_COUNTRY

export const ACCESS_TOKEN_COOKIE_NAME = get({
  key: 'ACCESS_TOKEN_COOKIE_NAME',
  value: process.env.ACCESS_TOKEN_COOKIE_NAME,
})

export const REFRESH_TOKEN_COOKIE_NAME = get({
  key: 'REFRESH_TOKEN_COOKIE_NAME',
  value: process.env.REFRESH_TOKEN_COOKIE_NAME,
})

export const ACCESS_TOKEN_KISSKISS_COOKIE_NAME = `${ACCESS_TOKEN_COOKIE_NAME}_for_kisskissbankbank`
export const REFRESH_TOKEN_KISSKISS_COOKIE_NAME = `${REFRESH_TOKEN_COOKIE_NAME}_for_kisskissbankbank`

export const REFRESH_TOKEN_ENDPOINT = `${ULULE_ORIGINS.fr}/refresh/`

export const ULULE_API_URL = get({ key: 'ULULE_API_URL', value: process.env.ULULE_API_URL, optional: true })

export const ROOT_ULULE_API_URL =
  lens<UfeConfig>()
    .get('UFE')
    .get('api')
    .get('ulule')
    .get('url')
    .value(globalThis, { silent: process.env.NEXT_BUILD === 'true', forgive: true, trust: true }) ??
  `${ULULE_API_URL}/v1`

export const VERCEL_URL = get({
  key: 'VERCEL_URL',
  value: process.env.VERCEL_URL,
  optional: true,
  parse: (url) => (url.startsWith('http') ? url : `https://${url}`),
})

export const FACEBOOK_LOGIN_URL = '/social/login/facebook/'

export const GOOGLE_LOGIN_URL = '/social/login/google/'

export const PASSWORD_RESET_URL = '/password/reset/'

export const FORBIDDEN_URL = '/forbidden/'

export type CurrenciesRates = {
  [currencyCode: string]: number
}

export const CURRENCIES_RATES = lens<UfeConfig>()
  .get('CURRENCIES')
  .get('RATES')
  .value(globalThis, { silent: true, forgive: true, trust: true })

type RawAllowedCurrencies = {
  [code: string]: { name: string; symbol: string }
}

export const RAW_ALLOWED_CURRENCIES: RawAllowedCurrencies = {
  EUR: {
    symbol: '€',
    name: 'Euro',
  },
  USD: {
    symbol: '$USD',
    name: 'Dollar américain',
  },
  GBP: {
    symbol: '£',
    name: 'Livre sterling',
  },
  CAD: {
    symbol: '$CAD',
    name: 'Dollar canadien',
  },
  DKK: {
    symbol: 'kr DKK',
    name: 'Couronne danoise',
  },
  AUD: {
    symbol: '$AUD',
    name: 'Dollar australien',
  },
  SEK: {
    symbol: 'kr SEK',
    name: 'Couronne suédoise',
  },
  NZD: {
    symbol: '$NZD',
    name: 'Dollar néo zélandais',
  },
  NOK: {
    symbol: 'kr NOK',
    name: 'Couronne norvégienne',
  },
  CHF: {
    symbol: 'fr.',
    name: 'Franc suisse',
  },
}

export type CurrencySymbol = {
  separator: string
  symbol: string
  trigram: string
}

type CurrenciesSymbol = {
  [currencyCode: string]: CurrencySymbol
}

export const CURRENCIES_SYMBOL: CurrenciesSymbol = {
  USD: {
    symbol: '$',
    trigram: 'USD',
    separator: '',
  },
  EUR: {
    symbol: '€',
    trigram: 'EUR',
    separator: '',
  },
  GBP: {
    symbol: '£',
    trigram: 'GBP',
    separator: '',
  },
  DKK: {
    symbol: 'kr',
    trigram: 'DKK',
    separator: ' ',
  },
  NOK: {
    symbol: 'kr',
    trigram: 'NOK',
    separator: ' ',
  },
  AUD: {
    symbol: '$',
    trigram: 'AUD',
    separator: '',
  },
  CAD: {
    symbol: '$',
    trigram: 'CAD',
    separator: '',
  },
  CHF: {
    symbol: 'fr.',
    trigram: 'CHF',
    separator: '',
  },
  SEK: {
    symbol: 'kr',
    trigram: 'SEK',
    separator: '',
  },
  NZD: {
    symbol: '$',
    trigram: 'NZD',
    separator: '',
  },
}

type AllowedCurrencies = { [currencyCode: string]: CurrencySymbol & { name: string; shortLabel: string } }

export const ALLOWED_CURRENCIES = Object.entries(CURRENCIES_SYMBOL).reduce((allowedCurrencies, [code, currency]) => {
  allowedCurrencies[code] = {
    ...currency,
    name: RAW_ALLOWED_CURRENCIES[code].name,
    shortLabel: RAW_ALLOWED_CURRENCIES[code].symbol,
  }
  return allowedCurrencies
}, {} as AllowedCurrencies)

// since `owl-kit` rely on env variables, we have to import specifically the
// file needed (`accounting` that don't rely on env variables) to avoid circular
// dependencies
import { setCurrencies } from '@ulule/owl-kit-components/src/core/accounting/index.ts'
setCurrencies(CURRENCIES_SYMBOL)

export const ALLOWED_LANGUAGES = {
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  it: 'Italiano',
  pt: 'Português',
  nl: 'Nederlands',
  ca: 'Català',
}

export const FACEBOOK_ACCOUNTS: Partial<Record<api.Lang, string>> = {
  fr: 'ulule',
  en: 'ululeEN',
  nl: 'ululeNL',
  it: 'ululeIT',
  es: 'ululeESP',
  ca: 'ululeESP',
}

export const TWITTER_ACCOUNTS = {
  en: 'ulule',
  fr: 'ululeFR',
  pt: 'ululePT',
  es: 'ululeES',
  ca: 'ululeES',
  it: 'ululeIT',
  nl: 'ululeNL',
  de: 'ululeDE',
}

/**
 * APPS AND SERVICES
 */

export const FACEBOOK_APP_ID = get({
  key: 'FACEBOOK_APP_ID',
  value: process.env.FACEBOOK_APP_ID,
  optional: true,
})

export const MAPBOX_API_KEY = get({
  key: 'MAPBOX_API_KEY',
  value: process.env.MAPBOX_API_KEY,
  optional: true,
})

export const SENTRY_PUBLIC_DSN = get({
  key: 'SENTRY_PUBLIC_DSN',
  value: process.env.SENTRY_PUBLIC_DSN,
  optional: true,
})

/**
 * AUTH PAGES
 */

export const JOIN_COMPLETE_URL = '/join/complete/'
export const SIGNIN_URL =
  get({
    key: 'SIGNIN_URL',
    value: process.env.SIGNIN_URL,
    optional: true,
  }) ?? '/signin/'
export const SIGNOUT_URL =
  get({
    key: 'SIGNOUT_URL',
    value: process.env.SIGNOUT_URL,
    optional: true,
  }) ?? '/signout/'
export const SIGNUP_URL =
  get({
    key: 'SIGNUP_URL',
    value: process.env.SIGNUP_URL,
    optional: true,
  }) ?? '/signup/'

/**
 * MISC. PAGES
 */

export const CHANNELS_URL = '/channels/'
export const COOKIE_POLICY_URL = '/about/cookie-policy/'
export const DISCOVER_LANDING_URL = '/pages/discover/'
export const DISCOVER_URL = '/discover/'
export const DISCUSSIONS_URL = '/discussions/'
export const HOMEPAGE_URL = '/'
export const PRIVACY_URL = '/about/privacy/'
export const PROPOSAL_URL = '/projects/create/'
export const TERMS_OF_USE_URL = '/about/terms/'

export type UfeConfig = {
  UFE: {
    EULA_UPDATED_AT: string
    locale: api.Lang
    defaultLocale: string
    defaultCountry: string
    country: string
    api: {
      ulule: {
        url: string
      }
    }
    allowedLanguages: api.I18n
    allowedCurrencies: {
      [code: string]: {
        name: string
        symbol: string
      }
    }
    facebook: {
      appId: string
    }
    localeFiles: api.I18n
    localeFile: string
    supportedCountries: {
      [currencyCode: string]: {
        [countryCode: string]: string
      }
    }
    environment: Environment
    cookies: {
      access_token: string
      refresh_token: string
      tracking_token: string
      currency: string
    }
    countries: Country[]
    islands: {
      [countryCode: string]: string[]
    }
    notifications: {
      children: string
      closable: boolean
      tags: string
    }[]
  }
  CURRENCIES: {
    COUNTRIES: {
      [countryCode: string]: string
    }
    CURRENT: string
    RATES: {
      [currencyCode: string]: number
    }
    SELECTED?: string
    SYMBOLS: {
      [currencyCode: string]: CurrencySymbol
    }
  }
}

// GLOBALS

export const GIT_SHA = get({
  key: 'GIT_SHA',
  value: process.env.GIT_SHA,
  optional: true,
})

export const NODE_ENV = get({
  key: 'NODE_ENV',
  value: process.env.NODE_ENV,
  optional: true,
})

// FILES

const BASE_LOCALE_FILES = {
  en: 'https://s3-eu-west-1.amazonaws.com/com.ulule.assets/site/build/locales/en.json',
  ca: 'https://s3-eu-west-1.amazonaws.com/com.ulule.assets/site/build/locales/ca.json',
  de: 'https://s3-eu-west-1.amazonaws.com/com.ulule.assets/site/build/locales/de.json',
  es: 'https://s3-eu-west-1.amazonaws.com/com.ulule.assets/site/build/locales/es.json',
  fr: 'https://s3-eu-west-1.amazonaws.com/com.ulule.assets/site/build/locales/fr.json',
  it: 'https://s3-eu-west-1.amazonaws.com/com.ulule.assets/site/build/locales/it.json',
  nl: 'https://s3-eu-west-1.amazonaws.com/com.ulule.assets/site/build/locales/nl.json',
  pt: 'https://s3-eu-west-1.amazonaws.com/com.ulule.assets/site/build/locales/pt.json',
}

export const UFE_LOCALE_FILES = lens<UfeConfig>()
  .get('UFE')
  .get('localeFiles')
  .value(globalThis, { silent: true, forgive: true, trust: true })

export const UFE_LOCALE_FILE = lens<UfeConfig>()
  .get('UFE')
  .get('localeFile')
  .value(globalThis, { silent: true, forgive: true, trust: true })

export const UFE_LOCALE = lens<UfeConfig>()
  .get('UFE')
  .get('locale')
  .value(globalThis, { silent: true, forgive: true, trust: true })

export const LOCALE_FILES = Object.fromEntries(
  Object.entries({
    ...BASE_LOCALE_FILES,
    ...UFE_LOCALE_FILES,
    ...(UFE_LOCALE && UFE_LOCALE_FILE && { [UFE_LOCALE]: UFE_LOCALE_FILE }),
  }).map(([lang, url]) => {
    const urlObject = new URL(url, globalThis.location?.href)
    if (GIT_SHA) {
      urlObject.search = new URLSearchParams({ cachebuster: GIT_SHA }).toString()
    }
    return [lang, urlObject.href]
  }),
)

// DJANGO DATA

export const NOTIFICATIONS = lens<UfeConfig>()
  .get('UFE')
  .get('notifications')
  .value(globalThis, { silent: true, forgive: true, trust: true })

export const EULA_UPDATED_AT =
  get({ key: 'EULA_UPDATED_AT', value: process.env.EULA_UPDATED_AT, optional: true }) ??
  lens<UfeConfig>()
    .get('UFE')
    .get('EULA_UPDATED_AT')
    .value(globalThis, {
      silent: process.env.NEXT_BUILD === 'true',
      forgive: true,
    })

// CONTENTFUL
export const CONTENTFUL_ENVIRONMENT = get({
  key: 'CONTENTFUL_ENVIRONMENT',
  value: process.env.CONTENTFUL_ENVIRONMENT,
  optional: true,
})

export const CONTENTFUL_SPACE_ID = get({
  key: 'CONTENTFUL_SPACE_ID',
  value: process.env.CONTENTFUL_SPACE_ID,
  optional: true,
})

export const CONTENTFUL_DELIVERY_TOKEN_MASTER = get({
  key: 'CONTENTFUL_DELIVERY_TOKEN_MASTER',
  value: process.env.CONTENTFUL_DELIVERY_TOKEN_MASTER,
  optional: true,
})
